import moment from "moment";

// Boundary between the punch-exceptions API and the report screen.
// GET {domain}monitoring/punch-exceptions/
//
// Request params: event_time_from, event_time_to, company_id, department_id.
// No paging: the server returns every row for the range, and the screen
// pages, filters by issue type / stage, and counts the cards itself.
// Response: { results, sort, counts: { ALL, unknown_employee, device,
// processing, issue_types: [{ value, label, category, count }] }, query }.
//
// The server echoes every param it accepted back in `query`. The date and
// company names are confirmed by that echo; department_id is not yet, so
// the screen checks the echo and filters the rows itself if it's missing
// (see unappliedFilters).

export const punchExceptionsUrl = (domain, params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") search.set(key, value);
  });
  return `${domain}monitoring/punch-exceptions/?${search}`;
};

// Pill colour per issue code: hard failures red, rule rejections
// (duplicate / too-close punches) blue, the rest amber.
const ERROR_ISSUES = new Set(["ATTENDANCE_FAILED", "DELIVERY_FAILED", "TARGETING_FAILED"]);
const INFO_ISSUES = new Set(["DUPLICATE", "MIN_DIFFERENCE", "MIN_IN_OUT_DIFFERENCE"]);
export const issueTone = (issueType) =>
  ERROR_ISSUES.has(issueType) ? "error" : INFO_ISSUES.has(issueType) ? "info" : "warning";

const text = (value, fallback = "-") =>
  value === null || value === undefined || value === "" ? fallback : String(value);

const formatDateTime = (value) => {
  const parsed = moment(value, moment.ISO_8601);
  return parsed.isValid() ? parsed.format("DD MMM YYYY HH:mm:ss") : text(value);
};

export const mapPunchExceptionRow = (row = {}) => {
  const isUnknownEmployee = !row.employee_name && !row.employee_code;
  const employeeName = isUnknownEmployee ? "Unknown employee" : text(row.employee_name);
  const deviceName = text(row.device_name);
  const punchDate = moment(row.punch_entry_date, "YYYY-MM-DD", true);

  return {
    id: row.id ?? row.global_event_id,
    event_id: text(row.global_event_id ?? row.id),
    employee_name: employeeName,
    employee_code: text(row.employee_code),
    employee_label: isUnknownEmployee
      ? employeeName
      : row.employee_code
        ? `${employeeName} (${row.employee_code})`
        : employeeName,
    is_unknown_employee: isUnknownEmployee,
    image_url: row.employee_image_url || undefined,
    department_id: row.department_id ?? null,
    department: text(row.department_name),
    punch_date: punchDate.isValid() ? punchDate.format("YYYY-MM-DD") : "",
    punch_date_display: punchDate.isValid() ? punchDate.format("DD MMM YYYY") : "-",
    punch_time: text(row.punch_entry_time),
    direction: text(row.direction).toUpperCase(),
    source: text(row.source),
    device_name: deviceName,
    device_code: text(row.device_id, ""),
    device_label: row.device_id ? `${deviceName} (${row.device_id})` : deviceName,
    issue_type: row.issue_type || "",
    issue: text(row.issue),
    issue_detail: row.issue_detail || "",
    issue_category: row.issue_category || "",
    processing_stage: text(row.processing_stage),
    status: text(row.status),
    attempt_count: row.attempt_count ?? null,
    event_time: formatDateTime(row.event_time),
    received_at: formatDateTime(row.received_at),
  };
};

export const mapPunchExceptionResponse = (response = {}) => {
  const issueTypes = response.counts?.issue_types;
  return {
    rows: (Array.isArray(response.results) ? response.results : []).map(mapPunchExceptionRow),
    issueTypes: Array.isArray(issueTypes) ? issueTypes : [],
    acceptedParams: Object.keys(response.query || {}),
  };
};

// Summary cards, counted from the rows on screen (after the Issue Type /
// Processing Stage filters) by the API's issue_category.
export const countByCategory = (rows) =>
  rows.reduce(
    (counts, row) => {
      counts.total += 1;
      if (row.issue_category === "unknown_employee") counts.employee += 1;
      else if (row.issue_category === "device") counts.device += 1;
      else if (row.issue_category === "processing") counts.processing += 1;
      return counts;
    },
    { total: 0, employee: 0, device: 0, processing: 0 },
  );

// Filters the server did not echo back in `query` were ignored by it.
export const unappliedFilters = (sentFilters, acceptedParams) =>
  Object.keys(sentFilters).filter(
    (key) => sentFilters[key] !== null && sentFilters[key] !== undefined && !acceptedParams.includes(key),
  );
